import { useEffect, RefObject } from 'react';
import { useMagicSearch, useMagicSearchContext } from './MagicSearchContext';
import { bindKey, unbindKey, bindKeyCombo, unbindKeyCombo, checkKey } from '@rwh/keystrokes';
import { scrollIntoViewOptions, RECENT_SEARCHES_SECTION_TITLE } from './constants';
import { SearchStore } from './SearchStore';
import { useUser } from 'react-scale/components/ui/UserContext';

function isEnterCombo() {
  return checkKey('Meta') || checkKey('Shift');
}

function canFocusChild(child: Element) {
  return (
    (child instanceof HTMLAnchorElement || child instanceof HTMLButtonElement) &&
    !child.getAttribute('disabled')
  );
}

function enterCommon(
  searchStore: SearchStore,
  listRef: RefObject<HTMLDivElement> | null,
): { listChild: Element } {
  const { focusedListItemIndex } = searchStore.getState();
  const listChildren = listRef?.current?.children ?? [];
  let listChild = listChildren[focusedListItemIndex];

  // return listChild to click for default enter
  if (focusedListItemIndex === -1) {
    let i = 0;
    if (listChildren[i].textContent === RECENT_SEARCHES_SECTION_TITLE) {
      // This code is a little brittle in the sense that we are assuming that the header is focusable then not focusable
      i++;
      while (i < listChildren.length && canFocusChild(listChildren[i])) {
        i++;
      }
    }

    for (; i < listChildren.length; i++) {
      if (canFocusChild(listChildren[i])) {
        listChild = listChildren[i];
        break;
      }
    }
  }

  return { listChild };
}

export function StartEvents() {
  const { listRef, searchStore } = useMagicSearchContext();
  const user = useUser();
  const { isOpen } = useMagicSearch(s => ({
    isOpen: s.isOpen,
  }));

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function removeFirstScope() {
      const { state } = searchStore!.getState();

      if (!state.searchText && state.searchScopes.length) {
        searchStore!
          .getState()
          .onRemoveSearchScope(state.searchScopes[state.searchScopes.length - 1].id);
      }
    }

    bindKey('Backspace', removeFirstScope);

    function moveFocusUp(event: any) {
      event.preventDefault();
      const { focusedListItemIndex } = searchStore!.getState();
      const listChildren = listRef?.current?.children ?? [];
      let prevFocusedListItemIndex = -1;

      for (let i = listChildren.length - 1; i > -1; i--) {
        const listChild = listChildren[i];
        if (canFocusChild(listChild) && focusedListItemIndex > i) {
          prevFocusedListItemIndex = i;
          break;
        }
      }

      if (prevFocusedListItemIndex === -1) {
        for (let i = listChildren.length - 1; i > -1; i--) {
          const listChild = listChildren[i];
          if (canFocusChild(listChild)) {
            prevFocusedListItemIndex = i;
            break;
          }
        }
      }

      const child = listChildren[prevFocusedListItemIndex];

      child?.scrollIntoView(scrollIntoViewOptions);

      return searchStore?.setState({
        focusedListItemIndex: prevFocusedListItemIndex,
      });
    }

    bindKey('ArrowUp', moveFocusUp);

    function moveFocusDown(event: any) {
      event.preventDefault();
      const { focusedListItemIndex } = searchStore!.getState();
      const listChildren = listRef?.current?.children ?? [];
      let nextFocusedListItemIndex = -1;

      for (let i = 0; i < listChildren.length; i++) {
        const listChild = listChildren[i];
        if (canFocusChild(listChild) && focusedListItemIndex < i) {
          nextFocusedListItemIndex = i;
          break;
        }
      }

      if (nextFocusedListItemIndex === -1) {
        for (let i = 0; i < listChildren.length; i++) {
          const listChild = listChildren[i];
          if (canFocusChild(listChild)) {
            nextFocusedListItemIndex = i;
            break;
          }
        }
      }

      const child = listChildren[nextFocusedListItemIndex];

      child?.scrollIntoView(scrollIntoViewOptions);

      return searchStore?.setState({
        focusedListItemIndex: nextFocusedListItemIndex,
      });
    }

    bindKey('ArrowDown', moveFocusDown);

    function openFocused(event: any) {
      if (!isEnterCombo()) {
        const { listChild } = enterCommon(searchStore!, listRef);

        if (listChild instanceof HTMLAnchorElement || listChild instanceof HTMLButtonElement) {
          listChild.click();
        }
        event.preventDefault();
      }
    }

    bindKey('Enter', openFocused);

    function openNewTabFocused({ finalKeyEvent }: any) {
      const { listChild } = enterCommon(searchStore!, listRef);

      if (listChild instanceof HTMLAnchorElement || listChild instanceof HTMLButtonElement) {
        const clickEvent = new MouseEvent('click', {
          metaKey: true,
        });
        listChild.dispatchEvent(clickEvent);
      }
      finalKeyEvent.preventDefault();
    }

    bindKeyCombo('Meta + Enter', openNewTabFocused);

    function openNewWindowFocused({ finalKeyEvent }: any) {
      const { listChild } = enterCommon(searchStore!, listRef);

      if (listChild instanceof HTMLAnchorElement || listChild instanceof HTMLButtonElement) {
        const clickEvent = new MouseEvent('click', {
          shiftKey: true,
        });
        listChild.dispatchEvent(clickEvent);
      }
      finalKeyEvent.preventDefault();
    }

    bindKeyCombo('Shift + Enter', openNewWindowFocused);

    return () => {
      unbindKey('Backspace', removeFirstScope);
      unbindKey('ArrowUp', moveFocusUp);
      unbindKey('ArrowDown', moveFocusDown);
      unbindKey('Enter', openFocused);
      unbindKeyCombo('Meta + Enter', openNewTabFocused);
      unbindKeyCombo('Shift + Enter', openNewWindowFocused);
    };
  }, [isOpen, listRef, searchStore, user]);

  return null;
}
